import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent } from './ui/dialog';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { X, Lock, Sparkles, ArrowRight, FileText, Database, FormInput, FilePen, HardDrive } from 'lucide-react';
import { useUpgradeModal } from '../contexts/UpgradeModalContext';

const LIMIT_META = {
  forms:                  { icon: FormInput, title: 'Form limit reached',         friendly: 'forms' },
  fields_per_form:        { icon: FileText,  title: 'Fields-per-form limit',      friendly: 'fields per form' },
  submissions_this_month: { icon: Database,  title: 'Monthly submission limit',   friendly: 'submissions this month' },
  signed_docs_this_month: { icon: FilePen,   title: 'Signed-document limit',      friendly: 'signed documents this month' },
  storage:                { icon: HardDrive, title: 'Storage limit reached',      friendly: 'storage' },
  subscription:           { icon: Lock,      title: 'Subscription expired',       friendly: 'subscription' },
};

const formatStorage = (bytes) => {
  if (!bytes && bytes !== 0) return '—';
  if (bytes >= 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${bytes} B`;
};

export const UpgradeModal = () => {
  const { payload, close } = useUpgradeModal();
  const navigate = useNavigate();

  if (!payload) return null;

  const meta = LIMIT_META[payload.limit_type] || LIMIT_META.forms;
  const Icon = meta.icon;
  const isStorage = payload.limit_type === 'storage';
  const isSubscriptionExpired = payload.error === 'subscription_expired';
  const currentLabel = isStorage ? formatStorage(payload.current) : String(payload.current);
  const limitLabel   = isStorage ? formatStorage(payload.limit)   : String(payload.limit);
  const percent      = payload.limit > 0 ? Math.min(100, Math.round((payload.current / payload.limit) * 100)) : 100;

  const isTopTier = !payload.upgrade_to; // pro -> Enterprise contact-sales path
  const upgradeCta = isSubscriptionExpired
    ? 'Renew or upgrade'
    : (isTopTier ? 'Contact Sales' : `Upgrade to ${payload.upgrade_to}`);

  const handleUpgrade = () => {
    close();
    if (isTopTier) {
      window.location.assign('mailto:sales@formlio.app?subject=Formlio%20Enterprise%20Inquiry');
    } else {
      navigate('/billing');
    }
  };

  const handleViewPlans = () => {
    close();
    navigate('/pricing');
  };

  return (
    <Dialog open={!!payload} onOpenChange={(o) => !o && close()}>
      <DialogContent
        className="sm:max-w-md p-0 overflow-hidden border-0 shadow-2xl"
        data-testid="upgrade-modal"
      >
        {/* Header band */}
        <div className="relative bg-gradient-to-br from-indigo-600 via-violet-600 to-purple-700 px-6 pt-6 pb-8 text-white">
          <button
            onClick={close}
            data-testid="upgrade-modal-close"
            className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center transition"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>

          <div className="w-14 h-14 rounded-2xl bg-white/15 backdrop-blur ring-1 ring-white/20 flex items-center justify-center mb-4">
            <Icon className="w-7 h-7" strokeWidth={2.2} />
          </div>

          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-indigo-100 mb-1">
            <Lock className="w-3.5 h-3.5" /> {isSubscriptionExpired ? 'Subscription expired' : `${payload.plan_display_name} plan limit`}
          </div>
          <h2 className="text-xl font-bold leading-tight" data-testid="upgrade-modal-title">
            {isSubscriptionExpired
              ? `Your ${payload.plan_display_name} subscription has expired`
              : `You've hit your ${payload.plan_display_name} ${meta.friendly} limit`}
          </h2>
        </div>

        {/* Body */}
        <div className="px-6 py-5 bg-white space-y-5">
          <p className="text-sm text-slate-600 leading-relaxed" data-testid="upgrade-modal-message">
            {payload.message}
          </p>

          {/* Quota meter — hidden for subscription_expired since there's no quota */}
          {!isSubscriptionExpired && (
            <div data-testid="upgrade-modal-meter">
              <div className="flex items-center justify-between text-xs font-semibold text-slate-700 mb-1.5">
                <span className="capitalize">{meta.friendly}</span>
                <span className="font-mono">
                  <span className="text-rose-600">{currentLabel}</span>
                  <span className="text-slate-400"> / </span>
                  <span>{limitLabel}</span>
                </span>
              </div>
              <Progress value={percent} className="h-2 bg-slate-100" />
            </div>
          )}

          {/* CTAs */}
          <div className="flex flex-col gap-2 pt-1">
            <Button
              onClick={handleUpgrade}
              data-testid="upgrade-modal-upgrade-btn"
              className="w-full bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 text-white font-semibold py-2.5"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              {upgradeCta}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button
              onClick={handleViewPlans}
              data-testid="upgrade-modal-view-plans-btn"
              variant="ghost"
              className="w-full text-slate-600 hover:text-slate-900"
            >
              Compare all plans
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
